import { useEffect, useMemo, useState, type FormEvent } from "react";
import { AdminNav } from "@/components/admin/AdminNav";
import AccessReportIssue from "@/pages/AccessReportIssue";
import {
  formatReportDate,
  useAdminAccessReportIssue,
  useAdminAccessReportIssues,
  useComputeAccessReport,
  usePublishAccessReport,
  useUpdateAccessReport,
  type AccessReportNarrative,
} from "@/lib/access-report";
import { SEO } from "@/lib/seo";
import "@/styles/brand-a.css";
import "@/styles/admin-invites.css";
import "@/styles/access-report.css";

const EMPTY_NARRATIVE: AccessReportNarrative = {
  intro: "",
  keyFindings: [""],
  methodology: "",
  notes: "",
  pressLine: "",
};

export default function AdminAccessReport() {
  const issues = useAdminAccessReportIssues();
  const compute = useComputeAccessReport();
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    if (selectedId || !issues.data?.issues.length) return;
    setSelectedId(issues.data.issues[0]?.id ?? "");
  }, [issues.data?.issues, selectedId]);

  const computeDraft = () => {
    compute.mutate(undefined, {
      onSuccess: ({ issue }) => setSelectedId(issue.id),
    });
  };

  return (
    <div className="brand-a pb-admin-invites-page pb-access-admin">
      <SEO
        title="Access Report admin · PulseBoard"
        description="Compute, edit, preview and publish Specialist Access Reports."
        path="/admin/access-report"
        noindex
      />
      <main className="pb-admin-invites-main">
        <AdminNav />
        <header className="pb-admin-heading">
          <div>
            <p>Admin · Quarterly dataset</p>
            <h1>Access Report</h1>
          </div>
          <button type="button" disabled={compute.isPending} onClick={computeDraft}>
            {compute.isPending ? "Computing…" : "Compute draft"}
          </button>
        </header>

        {compute.isError ? (
          <p className="pb-admin-state pb-admin-state--error" role="alert">
            The snapshot could not be computed. Existing published data was not changed.
          </p>
        ) : null}

        <section className="pb-access-admin__issues" aria-labelledby="admin-access-issues-heading">
          <div className="pb-admin-section-heading">
            <div>
              <h2 id="admin-access-issues-heading">Issues</h2>
              <p>Published data is frozen; narrative remains editable.</p>
            </div>
          </div>
          {issues.isLoading ? <p className="pb-admin-state">Loading Access Report issues…</p> : null}
          {issues.isError ? (
            <div className="pb-admin-state pb-admin-state--error" role="alert">
              Issues could not be loaded. <button type="button" onClick={() => void issues.refetch()}>Retry</button>
            </div>
          ) : null}
          {issues.data?.issues.length === 0 ? (
            <p className="pb-admin-state">No issues yet. Compute the baseline draft to begin.</p>
          ) : null}
          {issues.data?.issues.length ? (
            <div className="pb-access-admin__issue-list">
              {issues.data.issues.map((issue) => (
                <button
                  type="button"
                  key={issue.id}
                  className={selectedId === issue.id ? "is-selected" : ""}
                  onClick={() => setSelectedId(issue.id)}
                >
                  <span>
                    <strong>{issue.title}</strong>
                    <small>Issue No.{issue.issueNo} · computed {formatReportDate(issue.computedAt, true)}</small>
                  </span>
                  <span className="pb-access-admin__status" data-status={issue.status}>{issue.status}</span>
                </button>
              ))}
            </div>
          ) : null}
        </section>

        {selectedId ? <AccessReportEditor id={selectedId} /> : null}
      </main>
    </div>
  );
}

function AccessReportEditor({ id }: { id: string }) {
  const detail = useAdminAccessReportIssue(id);
  const update = useUpdateAccessReport();
  const publish = usePublishAccessReport();
  const [title, setTitle] = useState("");
  const [narrative, setNarrative] = useState<AccessReportNarrative>(EMPTY_NARRATIVE);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [publishedNotice, setPublishedNotice] = useState("");

  useEffect(() => {
    const issue = detail.data?.issue;
    if (!issue) return;
    setTitle(issue.title);
    setNarrative({
      ...EMPTY_NARRATIVE,
      ...issue.narrative,
      keyFindings: issue.narrative.keyFindings.length
        ? [...issue.narrative.keyFindings]
        : [""],
    });
    setShowPreview(false);
    setConfirmPublish(false);
    setPublishedNotice("");
  }, [detail.data?.issue]);

  const issue = detail.data?.issue;
  const previewIssue = useMemo(
    () => issue ? { ...issue, title, narrative } : null,
    [issue, narrative, title],
  );

  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!issue) return;
    update.mutate({ id: issue.id, title: title.trim(), narrative: cleanNarrative(narrative) });
  };

  const publishIssue = async () => {
    if (!issue) return;
    try {
      await update.mutateAsync({
        id: issue.id,
        title: title.trim(),
        narrative: cleanNarrative(narrative),
      });
      const result = await publish.mutateAsync(issue.id);
      setConfirmPublish(false);
      setPublishedNotice(`Published at /access-report/${result.slug}`);
    } catch {
      // Mutation state renders the actionable error below.
    }
  };

  if (detail.isLoading) return <p className="pb-admin-state pb-access-admin__editor-state">Loading issue editor…</p>;
  if (detail.isError || !issue) {
    return (
      <div className="pb-admin-state pb-admin-state--error pb-access-admin__editor-state" role="alert">
        This issue could not be loaded. <button type="button" onClick={() => void detail.refetch()}>Retry</button>
      </div>
    );
  }

  return (
    <section className="pb-access-admin__editor" aria-labelledby="admin-access-editor-heading">
      <div className="pb-admin-section-heading">
        <div>
          <h2 id="admin-access-editor-heading">Edit issue No.{issue.issueNo}</h2>
          <p>{issue.status === "published" ? "Narrative edits remain available; snapshot data is frozen." : "Review the narrative and preview before publishing."}</p>
        </div>
        <button type="button" onClick={() => setShowPreview((current) => !current)}>
          {showPreview ? "Close preview" : "Preview"}
        </button>
      </div>

      <form className="pb-access-admin__form" onSubmit={save}>
        <label>
          Title
          <input required maxLength={180} value={title} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <label>
          Introduction
          <textarea rows={5} value={narrative.intro} onChange={(event) => setNarrativeField("intro", event.target.value, setNarrative)} />
        </label>

        <fieldset>
          <legend>Key findings</legend>
          <div className="pb-access-admin__findings">
            {narrative.keyFindings.map((finding, index) => (
              <div key={index}>
                <label>
                  Finding {index + 1}
                  <textarea
                    rows={3}
                    value={finding}
                    onChange={(event) => setNarrative((current) => ({
                      ...current,
                      keyFindings: current.keyFindings.map((item, itemIndex) => itemIndex === index ? event.target.value : item),
                    }))}
                  />
                </label>
                <button
                  type="button"
                  disabled={narrative.keyFindings.length === 1}
                  onClick={() => setNarrative((current) => ({
                    ...current,
                    keyFindings: current.keyFindings.filter((_item, itemIndex) => itemIndex !== index),
                  }))}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setNarrative((current) => ({
              ...current,
              keyFindings: [...current.keyFindings, ""],
            }))}
          >
            Add finding
          </button>
        </fieldset>

        <label>
          Methodology
          <textarea rows={8} value={narrative.methodology} onChange={(event) => setNarrativeField("methodology", event.target.value, setNarrative)} />
        </label>
        <label>
          Notes
          <textarea rows={5} value={narrative.notes} onChange={(event) => setNarrativeField("notes", event.target.value, setNarrative)} />
        </label>
        <label>
          Press line
          <textarea rows={3} value={narrative.pressLine} onChange={(event) => setNarrativeField("pressLine", event.target.value, setNarrative)} />
        </label>

        <div className="pb-access-admin__form-actions">
          <button type="submit" disabled={update.isPending || !title.trim()}>
            {update.isPending ? "Saving…" : "Save narrative"}
          </button>
          {issue.status === "draft" ? (
            <button type="button" className="pb-access-admin__publish" onClick={() => setConfirmPublish(true)}>
              Publish
            </button>
          ) : null}
          {update.isSuccess && !publish.isPending ? <span role="status">Saved.</span> : null}
          {update.isError ? <span className="pb-access-admin__error" role="alert">Changes could not be saved.</span> : null}
          {publishedNotice ? <span role="status">{publishedNotice}</span> : null}
        </div>
      </form>

      {confirmPublish ? (
        <div className="pb-access-admin__confirm" role="alertdialog" aria-modal="true" aria-labelledby="access-publish-confirm-heading">
          <div>
            <h3 id="access-publish-confirm-heading">Publish issue No.{issue.issueNo}?</h3>
            <p>Publishing makes this issue public and freezes its data snapshot permanently. Narrative text can still be edited.</p>
            <div>
              <button type="button" onClick={() => setConfirmPublish(false)} disabled={publish.isPending}>Cancel</button>
              <button type="button" onClick={() => void publishIssue()} disabled={publish.isPending || update.isPending || !title.trim()}>
                {publish.isPending || update.isPending ? "Publishing…" : "Confirm publish"}
              </button>
            </div>
            {publish.isError ? <p className="pb-access-admin__error">The issue could not be published.</p> : null}
          </div>
        </div>
      ) : null}

      {showPreview && previewIssue ? (
        <section className="pb-access-admin__preview" aria-labelledby="admin-access-preview-heading">
          <h2 id="admin-access-preview-heading">Public issue preview</h2>
          <AccessReportIssue issue={previewIssue} preview />
        </section>
      ) : null}
    </section>
  );
}

function setNarrativeField(
  field: Exclude<keyof AccessReportNarrative, "keyFindings">,
  value: string,
  setNarrative: React.Dispatch<React.SetStateAction<AccessReportNarrative>>,
) {
  setNarrative((current) => ({ ...current, [field]: value }));
}

function cleanNarrative(narrative: AccessReportNarrative): AccessReportNarrative {
  const keyFindings = narrative.keyFindings.map((finding) => finding.trim()).filter(Boolean);
  return {
    intro: narrative.intro.trim(),
    keyFindings: keyFindings.length ? keyFindings : [""],
    methodology: narrative.methodology.trim(),
    notes: narrative.notes.trim(),
    pressLine: narrative.pressLine.trim(),
  };
}
